import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import {
  usePutAdminEnrollmentEventEnrollmentEventId,
} from '../../../utils/web_api'
import {
  type AdminEnrollmentEvent,
  type PutAdminEnrollmentEventEnrollmentEventId,
} from '../../../utils/web_api.schemas'

export function useUpdateEnrollmentEvent (employeeId: string) {
  const queryClient = useQueryClient()

  const updateMutation = usePutAdminEnrollmentEventEnrollmentEventId({
    mutation: {
      onMutate: async (variables) => {
        await queryClient.cancelQueries({
          queryKey: ['/api/web/admin/enrollment_event'],
        })

        const previousData = queryClient.getQueryData<{ data: AdminEnrollmentEvent[] }>([
          '/api/web/admin/enrollment_event',
          { employee_id: employeeId },
        ])

        if (previousData) {
          queryClient.setQueryData<{ data: AdminEnrollmentEvent[] }>(
            ['/api/web/admin/enrollment_event', { employee_id: employeeId }],
            (old) => {
              if (!old) return old
              return {
                ...old,
                data: old.data.map((event) =>
                  event.id === variables.enrollmentEventId
                    ? { ...event, ...variables.data } as AdminEnrollmentEvent
                    : event
                ),
              }
            }
          )
        }

        return { previousData }
      },
      onError: (error: any, _variables, context) => {
        if (context?.previousData) {
          queryClient.setQueryData(
            ['/api/web/admin/enrollment_event', { employee_id: employeeId }],
            context.previousData
          )
        }
        toast.error(error?.message || 'Failed to update enrollment event')
      },
      onSuccess: () => {
        toast.success('Enrollment event updated!')
        queryClient.invalidateQueries({
          queryKey: ['/api/web/admin/enrollment_event'],
        })
      },
    },
  })

  const updateEnrollmentEvent = (
    id: string,
    updates: Omit<PutAdminEnrollmentEventEnrollmentEventId, 'employee_id'>
  ) => {
    updateMutation.mutate({
      enrollmentEventId: id,
      data: {
        ...updates,
        employee_id: employeeId,
      },
    })
  }

  return {
    updateMutation,
    updateEnrollmentEvent,
  }
}
